import React, { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Plus, FileText, Users, CalendarDays, Clock, CheckCircle2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PageHeader, StatCard, StatCardGroup } from '@/components/shared'
import { leaveService } from '@/services/leaveService'
import { useAuthStore } from '@/store/authStore'
import LeaveApplicationForm from '@/components/Leave/LeaveApplicationForm'
import LeaveHistoryTable from '@/components/Leave/LeaveHistoryTable'
import LeaveManagementTable from '@/components/Leave/LeaveManagementTable'

const Leaves = () => {
  const [activeTab, setActiveTab] = useState('my-leaves')
  const [showApplicationForm, setShowApplicationForm] = useState(false)
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  const isHRAdmin = user?.role === 'admin' || user?.role === 'hr'

  // Fetch leaves data
  const { data: leavesData, isLoading, error } = useQuery(
    'leaves',
    leaveService.getAllLeaves,
    {
      onError: (err) => {
        toast.error('Failed to fetch leave requests: ' + (err.response?.data?.error || err.message))
      }
    }
  )

  const leaves = leavesData?.data?.leaves || []

  // Filter leaves for current user (for "My Leave History" tab)
  const myLeaves = leaves.filter(leave => leave.employee?.user?.id === user?.id)

  // Calculate leave statistics
  const leaveStats = useMemo(() => {
    const pending = myLeaves.filter(l => l.status === 'pending').length
    const approved = myLeaves.filter(l => l.status === 'approved').length
    const total = myLeaves.length
    return { pending, approved, total }
  }, [myLeaves])

  const handleApplicationSuccess = () => {
    setShowApplicationForm(false)
    queryClient.invalidateQueries('leaves')
  }

  const handleApplicationCancel = () => {
    setShowApplicationForm(false)
  }

  const handleUpdate = () => {
    queryClient.invalidateQueries('leaves')
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Leave Management" 
        description="Track and manage your leave requests"
        actions={
          <Button size="sm" className="h-8 px-3 text-sm" onClick={() => setShowApplicationForm(true)}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            Apply for Leave
          </Button>
        }
      />

      {/* Leave Statistics */}
      <StatCardGroup>
        <StatCard
          title="Total Requests"
          value={leaveStats.total}
          description="All time leave requests"
          icon={CalendarDays}
        />
        <StatCard
          title="Pending"
          value={leaveStats.pending}
          description="Awaiting approval"
          icon={Clock}
          valueClassName="text-yellow-600"
        />
        <StatCard
          title="Approved"
          value={leaveStats.approved}
          description="Approved leaves"
          icon={CheckCircle2}
          valueClassName="text-emerald-600"
        />
      </StatCardGroup>

      {/* Application Form Modal */}
      <Dialog open={showApplicationForm} onOpenChange={setShowApplicationForm}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Apply for Leave</DialogTitle>
          </DialogHeader>
          <LeaveApplicationForm
            onSuccess={handleApplicationSuccess}
            onCancel={handleApplicationCancel}
          />
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="my-leaves" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            My Leave History
          </TabsTrigger>
          {isHRAdmin && (
            <TabsTrigger value="manage-requests" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Manage Requests
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="my-leaves" className="mt-6">
          <LeaveHistoryTable 
            leaves={myLeaves} 
            onUpdate={handleUpdate}
          />
        </TabsContent>

        {isHRAdmin && (
          <TabsContent value="manage-requests" className="mt-6">
            <LeaveManagementTable 
              leaves={leaves} 
              onUpdate={handleUpdate}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

export default Leaves
